/**
 * Tenant admin shell.
 *
 * The layout itself is `shared/layout/WorkspaceShell` — the same sidebar + top bar the
 * platform console uses. What stays here is everything that is specific to a TENANT
 * admin: which sections exist, which of them a persona may see, whose name is in the
 * chrome, and the account menu with its route back to the learner view.
 *
 * Sections are declared in one place below so adding a surface is one entry, and the nav
 * never advertises a page that does not exist.
 */

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { Persona, Session } from '../shared/lib/session';
import { signOut } from '../shared/lib/session';
import { getConfig } from '../shared/lib/config';
import { Icon } from '../shared/ui';
import { WorkspaceShell, type WorkspaceNavGroup } from '../shared/layout/WorkspaceShell';
import './admin.css';

/** A nav entry plus the one thing the shared shell has no opinion about: who may see it. */
interface AdminNavGroup extends WorkspaceNavGroup {
  items: (WorkspaceNavGroup['items'][number] & {
    /** Lowest persona that may see this. Absent means any admin-side persona. */
    min?: Persona;
  })[];
}

const NAV: AdminNavGroup[] = [
  {
    heading: 'Overview',
    items: [{ to: '/admin', label: 'Dashboard', icon: 'dashboard' }],
  },
  {
    heading: 'Content',
    items: [
      { to: '/admin/courses', label: 'Courses', icon: 'book_2' },
      { to: '/admin/paths', label: 'Learning Paths', icon: 'route' },
      { to: '/admin/library', label: 'Content Library', icon: 'inventory_2' },
    ],
  },
  {
    heading: 'People',
    items: [
      { to: '/admin/learners', label: 'Learners', icon: 'group' },
      { to: '/admin/team', label: 'Team', icon: 'badge', min: 'tenant_admin' },
      { to: '/admin/enrolments', label: 'Enrolments', icon: 'how_to_reg' },
    ],
  },
  {
    heading: 'Engagement',
    items: [
      { to: '/admin/discussions', label: 'Discussions', icon: 'forum' },
      { to: '/admin/announcements', label: 'Announcements', icon: 'campaign' },
    ],
  },
  {
    heading: 'Insight',
    items: [
      { to: '/admin/progress', label: 'Progress', icon: 'trending_up' },
      { to: '/admin/grades', label: 'Grades', icon: 'grade' },
      { to: '/admin/certificates', label: 'Certificates', icon: 'workspace_premium' },
    ],
  },
  // Money is its own heading rather than a line under "Insight", because these two pages are
  // the only ones in this shell that can move a learner's money or take their access away.
  //
  // `min: 'tenant_admin'` is a NAV decision and NOT a security boundary — the server decides,
  // and it decides per academy: `controls.operations` is absent from the payload for a caller
  // who may not write, and every write route answers 404 for an academy the deployment has not
  // enabled. Hiding the entry from staff keeps the nav from advertising a screen most of them
  // cannot act on; the screens themselves still state the reason when they are read-only.
  {
    heading: 'Money',
    items: [
      { to: '/admin/payments', label: 'Payments', icon: 'receipt_long', min: 'tenant_admin' },
      { to: '/admin/promotions', label: 'Codes', icon: 'local_activity', min: 'tenant_admin' },
      { to: '/admin/payments/audit', label: 'Payment audit', icon: 'history', min: 'tenant_admin' },
    ],
  },
  {
    heading: 'Configure',
    items: [{ to: '/admin/settings', label: 'Settings', icon: 'settings', min: 'tenant_admin' }],
  },
];

const RANK: Record<Persona, number> = {
  anonymous: 0,
  learner: 1,
  tenant_staff: 2,
  tenant_admin: 3,
  superadmin: 4,
};

export function AdminShell({ session, persona }: { session: Session; persona: Persona }) {
  const config = getConfig();

  const groups: WorkspaceNavGroup[] = NAV.map((group) => ({
    heading: group.heading,
    items: group.items.filter((item) => !item.min || RANK[persona] >= RANK[item.min]),
  })).filter((group) => group.items.length > 0);

  const initials = (session.user?.name || session.user?.username || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

  return (
    <WorkspaceShell
      storageKey="thinlms.admin.sidebar"
      brand={{
        mark: config.tenant.name.charAt(0).toUpperCase(),
        title: config.tenant.name,
        subtitle: 'Admin',
        home: '/admin',
      }}
      groups={groups}
      // Names the academy permanently. An admin tool that looks identical across
      // academies is how people edit the wrong one.
      scope={{ icon: 'apartment', label: config.tenant.name }}
      actions={
        <>
          <NavLink to="/" className="workspace__exit">
            <Icon name="logout" />
            Back to site
          </NavLink>
          <AdminAccount initials={initials} session={session} persona={persona} />
        </>
      }
    />
  );
}

function AdminAccount({
  initials,
  session,
  persona,
}: {
  initials: string;
  session: Session;
  persona: Persona;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="workspace__account">
      <button
        type="button"
        className="avatar"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        onClick={() => setOpen(!open)}
      >
        {initials}
      </button>

      {open && (
        <div className="lms-menu" role="menu">
          <div className="lms-menu__head">
            <strong>{session.user?.name || session.user?.username}</strong>
            <span>{session.user?.email}</span>
            <span className="lms-role">{persona === 'tenant_staff' ? 'Staff' : 'Admin'}</span>
          </div>
          <NavLink to="/" role="menuitem" onClick={() => setOpen(false)}>
            Learner view
          </NavLink>
          {persona === 'superadmin' && (
            <NavLink to="/superadmin" role="menuitem" onClick={() => setOpen(false)}>
              Platform console
            </NavLink>
          )}
          <button type="button" role="menuitem" onClick={signOut}>
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Re-exported, not redefined.
 *
 * A dozen admin pages import `PageHeader` from this module and the console's pages now
 * want the same one; it lives with the shell it belongs to, and this keeps those imports
 * working rather than editing twelve files to move an import line.
 */
export { PageHeader } from '../shared/layout/WorkspaceShell';
